import { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
  LayoutDashboard, Wallet, ShoppingBag, Users, LogOut, Menu, X, Plane, ChevronRight, Package, Shield, Clock,
  ShoppingCart, RefreshCw, History, Send
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import WhatsAppButton from '@/components/WhatsAppButton';
import ClientBottomBar from '@/components/ClientBottomBar';
import { useTheme } from '@/contexts/ThemeContext';

const ClientLayout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { dark, toggle } = useTheme();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/cliente/login');
  };

  const navItems = [
    { path: '/cliente', icon: LayoutDashboard, label: 'Inicio', end: true },
    { path: '/cliente/servicios', icon: ShoppingBag, label: 'Servicios' },
    { path: '/cliente/carrito', icon: ShoppingCart, label: 'Mi Carrito' },
    { path: '/cliente/divisas', icon: RefreshCw, label: 'Divisas' },
    { path: '/cliente/transferencias', icon: Send, label: 'Transferencias' },
    { path: '/cliente/ordenes', icon: Package, label: 'Mis Ordenes' },
    { path: '/cliente/wallet', icon: Wallet, label: 'Mi Wallet' },
    { path: '/cliente/historial', icon: History, label: 'Historial' },
    { path: '/cliente/referidos', icon: Users, label: 'Referidos' },
    { path: '/cliente/seguridad', icon: Shield, label: 'Seguridad' },
    { path: '/cliente/ayuda', icon: Clock, label: 'Ayuda' },
  ];

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      <aside className={`
        fixed lg:static inset-y-0 left-0 z-50 w-72 
        bg-gradient-to-b from-emerald-900 to-emerald-950
        transform transition-transform duration-300 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="flex flex-col h-full">
          <div className="p-5 border-b border-white/10">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-400 rounded-lg flex items-center justify-center">
                <Plane className="w-6 h-6 text-emerald-950" />
              </div>
              <div>
                <h1 className="font-bold text-white text-lg tracking-tight">Aircom Connect</h1>
                <p className="text-xs text-emerald-300">Portal de Cliente</p>
              </div>
            </div>
          </div>

          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.end}
                onClick={() => setSidebarOpen(false)}
                className={({ isActive }) => `
                  flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200
                  ${isActive
                    ? 'bg-emerald-400/20 text-emerald-200 shadow-sm'
                    : 'text-emerald-100/70 hover:bg-white/5 hover:text-white'}
                `}
                data-testid={`client-nav-${item.label.toLowerCase().replace(' ', '-')}`}
              >
                <item.icon className="w-5 h-5" />
                <span>{item.label}</span>
                <ChevronRight className="w-4 h-4 ml-auto opacity-50" />
              </NavLink>
            ))}
          </nav>

          <div className="p-4 border-t border-white/10">
            <div className="flex items-center gap-3 px-3 py-2">
              <div className="w-10 h-10 rounded-full bg-emerald-500 flex items-center justify-center text-white font-semibold">
                {user?.name?.charAt(0).toUpperCase() || 'C'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{user?.name}</p>
                <p className="text-xs text-emerald-300 truncate">Cliente</p>
              </div>
            </div>
            <Button
              variant="ghost"
              className="w-full justify-start gap-3 text-emerald-200 hover:text-white hover:bg-white/10 mt-2"
              onClick={handleLogout}
              data-testid="client-logout-btn"
            >
              <LogOut className="w-5 h-5" />
              Cerrar sesion
            </Button>
          </div>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-h-screen">
        <header className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700 px-4 lg:px-8 py-4 flex items-center gap-4 sticky top-0 z-30">
          <button
            className="lg:hidden p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            data-testid="client-mobile-menu-btn"
          >
            {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
          <div className="flex-1" />
          <button onClick={toggle} className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500" data-testid="dark-mode-toggle" title={dark ? 'Modo claro' : 'Modo oscuro'}>
            {dark ? (
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" /></svg>
            ) : (
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" /></svg>
            )}
          </button>
          <div className="text-sm text-slate-500 dark:text-slate-400">
            {new Date().toLocaleDateString('es-ES', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          </div>
        </header>

        <main className="flex-1 p-4 lg:p-8 pb-20 lg:pb-8">
          <Outlet />
        </main>
        <WhatsAppButton />
        <ClientBottomBar />
      </div>
    </div>
  );
};

export default ClientLayout;
